import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCheck, Undo2, Loader2 } from "lucide-react";
import { api, errMessage } from "../../lib/api";

const localNow = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

/** For posts the customer published by hand — keeps the calendar and insights honest. */
export const MarkAsPosted = ({ item, onDone }) => {
  const [open, setOpen] = useState(false);
  const [when, setWhen] = useState(localNow);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/social/mark-posted/${item.id}`, {
        posted_at: when ? new Date(when).toISOString() : null,
        post_url: url,
      });
      toast.success(data.message || "Marked as posted.");
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`mark-posted-open-${item.id}`}
        className="inline-flex items-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800 transition-colors hover:bg-emerald-100"
      >
        <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" /> I posted this
      </button>
    );
  }

  return (
    <div
      className="w-full rounded-2xl border border-emerald-200 bg-emerald-50/60 p-3"
      data-testid={`mark-posted-form-${item.id}`}
    >
      <p className="text-xs font-semibold text-emerald-900">When did you post it?</p>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
        <input
          type="datetime-local"
          value={when}
          onChange={(e) => setWhen(e.target.value)}
          data-testid={`mark-posted-when-${item.id}`}
          className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm sm:max-w-[15rem]"
        />
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Link to the post (optional)"
          data-testid={`mark-posted-url-${item.id}`}
          className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
        />
      </div>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          data-testid={`mark-posted-confirm-${item.id}`}
          className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-800 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCheck className="h-3.5 w-3.5" />}
          Mark as posted
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          data-testid={`mark-posted-cancel-${item.id}`}
          className="rounded-xl border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-white"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export const UndoMarkPosted = ({ item, onDone }) => {
  const [busy, setBusy] = useState(false);
  const undo = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/social/unmark-posted/${item.id}`);
      toast.success(data.message || "Moved back to approved.");
      onDone?.();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={undo}
      disabled={busy}
      data-testid={`unmark-posted-${item.id}`}
      className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}
      Undo
    </button>
  );
};

export default MarkAsPosted;
